import { VIDEO } from 'global/constants';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import type { GetStimulusResourceQuery } from '../../../shared/stimulus/stimulus.graphql.generated';
import { getClosestImageUrl } from 'approot/shared/images/imgix-responsive';
import StimulusUsageMessage from '../shared/stimulus-message';
import {
  SettingsTitleView,
  SettingsRichtextView,
  SettingsThumbnailView,
  SettingsCoverImageView,
} from '../shared/settings-view';
import { SettingsHr } from 'approot/activity-builder/settings/settings.style';
import { canShowAttributionModificationDescription } from 'approot/shared/stimulus/logic/can-show-attribution-modification-description';
import StimulusContentWarning from 'approot/shared/stimulus/stimulus-content-warning';
import { Heading16 } from 'approot/shared/styles/typography.style';
import { hasAttributionContent } from 'approot/shared/stimulus/logic/has-attribution-content';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  stimulus: Extract<
    Pluck<GetStimulusResourceQuery, 'getStimulusResource'>,
    { __typename: 'StimulusResourceVideo' }
  >;
  componentId: string;
  sectionId: string;
  lessonId: number;
};

const VideoEditPreview = ({
  stimulus,
  componentId,
  sectionId,
  lessonId,
}: Props) => {
  const { closeDrawer } = useActivityBuilderContext();

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader
        title={stimulus.type === VIDEO ? 'Video' : 'Audio'}
        onClose={closeDrawer}
      />
      <ActivityBuilderDrawerContent>
        <StimulusUsageMessage
          componentId={componentId}
          stimulusId={stimulus.id}
          sectionId={sectionId}
          contentId={lessonId}
        />
        <SettingsTitleView title="Link" value={stimulus.videos[0].link} />
        {stimulus.videos?.[1] && (
          <SettingsTitleView
            title="Backup Vimeo Link"
            value={stimulus.videos[1].link}
          />
        )}

        <SettingsTitleView title="Stimulus Name" value={stimulus.name} />
        <SettingsRichtextView
          title="Stimulus Description"
          value={stimulus.description}
        />
        <SettingsThumbnailView
          title="Stimulus Thumbnail"
          imageUrl={stimulus.coverImage?.responsive?.[0].urls.at(-1)?.url}
        />
        {stimulus.xlCoverImageUrls?.length ? (
          <SettingsCoverImageView
            title="Stimulus Cover in Activity Builder"
            imageUrl={getClosestImageUrl({
              imageUrls: stimulus.xlCoverImageUrls,
              containerSize: 300,
            })}
          />
        ) : null}
        {!!stimulus.durationMinutes && (
          <SettingsTitleView
            title={stimulus.type === VIDEO ? 'Video Length' : 'Audio Length'}
            value={`${stimulus.durationMinutes} minutes`}
          />
        )}

        {stimulus.attributions &&
          (hasAttributionContent(stimulus.attributions) ||
            stimulus.attributions?.contentWarning) && (
            <>
              <SettingsHr />
              {stimulus.attributions.contentWarning && (
                <>
                  <Heading16 as="h2" marginBottom={8}>
                    Content Warning
                  </Heading16>
                  <StimulusContentWarning />
                </>
              )}
              {stimulus.attributions.author && (
                <SettingsTitleView
                  title="Author"
                  value={stimulus.attributions.author}
                />
              )}
              {stimulus.attributions.copyright && (
                <SettingsTitleView
                  title="Copyright"
                  value={stimulus.attributions.copyright ?? 'Not indicated.'}
                />
              )}
              {stimulus.attributions.licence && (
                <SettingsTitleView
                  title="License"
                  value={stimulus.attributions.licence}
                />
              )}
              {stimulus.attributions.modifications && (
                <SettingsTitleView
                  title="Modifications"
                  value={stimulus.attributions.modifications}
                />
              )}
              {canShowAttributionModificationDescription(
                stimulus.attributions
              ) && (
                <SettingsTitleView
                  title="Modification Description"
                  value={stimulus.attributions.modificationDescription}
                />
              )}
            </>
          )}
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default VideoEditPreview;
